import { HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import { useRef } from 'react';
import { LuArrowLeft } from 'react-icons/lu';

import Drawer from '@/components/ui/Drawer/Drawer';
import { TAgent, TAssignedCustomers, TRunAgentParams } from '@/types/agents';
import { EAgentService } from '@/types/agents';

import ConfigureStage, { ConfigureStageRef } from './stages/ConfigureStage/ConfigureStage';
import DetailsStage from './stages/DetailsStage/DetailsStage';
import InputStage from './stages/InputStage/InputStage';
import useAgentDetailsSidebar, { SidebarStage } from './useAgentDetailsSidebar';

const renderConfigureHeader = (
  handleStageChange: (stage: SidebarStage) => void,
  configureStageRef: React.RefObject<ConfigureStageRef | null>
) => {
  const handleBackToDetails = () => {
    if (configureStageRef.current) {
      configureStageRef.current.requestLeave(() => handleStageChange(SidebarStage.Details));
    } else {
      handleStageChange(SidebarStage.Details);
    }
  };

  return (
    <HStack gap={4} width="100%">
      <IconButton
        variant="ghost"
        size="sm"
        onClick={handleBackToDetails}
        aria-label="Back"
        _hover={{ backgroundColor: 'gray.100' }}
      >
        <Icon as={LuArrowLeft} boxSize={5} color="gray.600" />
      </IconButton>
      <Text fontSize={24} fontWeight={500} color="fg">
        Configure Agent
      </Text>
    </HStack>
  );
};

const renderInputHeader = (
  handleStageChange: (stage: SidebarStage) => void,
  isNativeAgent: boolean
) => {
  const handleBackFromInput = () => {
    handleStageChange(isNativeAgent ? SidebarStage.Details : SidebarStage.Configure);
  };

  return (
    <HStack gap={4} width="100%" justifyContent="space-between">
      <HStack gap={4}>
        <IconButton
          variant="ghost"
          size="sm"
          onClick={handleBackFromInput}
          aria-label="Back"
          _hover={{ backgroundColor: 'gray.100' }}
        >
          <Icon as={LuArrowLeft} boxSize={5} color="gray.600" />
        </IconButton>
        <Text fontSize={24} fontWeight={500} color="fg">
          Inputs
        </Text>
      </HStack>
    </HStack>
  );
};

interface AgentDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  agent: TAgent | null;
  onRemoveCustomer: (customerId: string) => void;
  onAssignMoreCustomers: () => void;
  assignedCustomers: TAssignedCustomers[];
  onSave: () => void;
  onRunAgent: (params: TRunAgentParams) => void;
  isLoadingAssignAgentToCustomers: boolean;
}

const AgentDetailsSidebar = ({
  isOpen,
  onClose,
  agent,
  onRemoveCustomer,
  onAssignMoreCustomers,
  assignedCustomers,
  onSave,
  onRunAgent,
  isLoadingAssignAgentToCustomers,
}: AgentDetailsSidebarProps) => {

  const configureStageRef = useRef<ConfigureStageRef | null>(null);
  const isNativeAgent = agent?.service === EAgentService.NATIVE;

  const {
    stage,
    configId,
    setConfigId,
    selectedCredentials,
    setSelectedCredentials,
    inputValues,
    filesRef,
    handleInputValueChange,
    handleFileChange,
    handleStageChange,
    handleCredentialChange,
    handleConfigContinue,
    handleDrawerClose: originalHandleDrawerClose,
    handleRunTest,
  } = useAgentDetailsSidebar({
    agent,
    onRunAgent,
    onClose,
  });

  const handleDrawerClose = () => {
    if (stage === SidebarStage.Configure && configureStageRef.current) {
      configureStageRef.current.requestLeave(originalHandleDrawerClose);
    } else {
      originalHandleDrawerClose();
    }
  };

  const handleNavigateToRun = () => {
    handleStageChange(isNativeAgent ? SidebarStage.Input : SidebarStage.Configure);
  };

  if (!agent) {
    return null;
  }

  const initialCustomerIds = (agent.assignCustomers || []).map((c) => c.customerId).sort();
  const currentCustomerIds = assignedCustomers.map((c) => c.customerId).sort();
  const hasCustomersChanged = JSON.stringify(initialCustomerIds) !== JSON.stringify(currentCustomerIds);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleDrawerClose}
      title={stage === SidebarStage.Details ? 'Agent Information' : undefined}
      customHeader={
        stage === SidebarStage.Configure
          ? renderConfigureHeader(handleStageChange, configureStageRef)
          : stage === SidebarStage.Input
            ? renderInputHeader(handleStageChange, isNativeAgent)
            : undefined
      }
      size="lg"
    >
      {stage === SidebarStage.Details && (
        <DetailsStage
          agent={agent}
          assignedCustomers={assignedCustomers}
          onRemoveCustomer={onRemoveCustomer}
          onAssignMoreCustomers={onAssignMoreCustomers}
          hasCustomersChanged={hasCustomersChanged}
          onSave={onSave}
          onNavigateToConfigure={handleNavigateToRun}
          isLoadingAssignAgentToCustomers={isLoadingAssignAgentToCustomers}
        />
      )}
      {stage === SidebarStage.Configure && (
        <ConfigureStage
          ref={configureStageRef}
          agent={agent}
          configId={configId}
          onConfigNameChange={setConfigId}
          selectedCredentials={selectedCredentials}
          onCredentialChange={handleCredentialChange}
          setSelectedCredentials={setSelectedCredentials}
          onContinue={handleConfigContinue}
        />
      )}
      {stage === SidebarStage.Input && (
        <InputStage
          agent={agent}
          inputValues={inputValues}
          files={filesRef.current}
          onInputValueChange={handleInputValueChange}
          onFileChange={handleFileChange}
          onRunTest={handleRunTest}
        />
      )}
    </Drawer>
  );
};

export default AgentDetailsSidebar;